import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import cloneDeep from 'lodash/cloneDeep';

interface ShiftOneCExportState {
    loading: boolean;
    loaded: boolean;
    fatalError: boolean;
    retrying: boolean;
    documentId: any;
    packages: any[];
}

const state: ShiftOneCExportState = {
    loading: false,
    loaded: false,
    fatalError: false,
    retrying: false,
    documentId: null,
    packages: [],
};

const initState: any = cloneDeep(state);

const ONEC_PACKAGES = 'ONEC_PACKAGES';

// подписи статусов пакета (раздел 5.1 ТЗ «Расчёт расхода сырья по партиям»)
export const PACKAGE_STATUS: any = {
    calculating: { label: 'Рассчитывается', type: 'is-warning' },
    calculated: { label: 'Рассчитан', type: 'is-info' },
    sending: { label: 'Отправляется', type: 'is-warning' },
    sent: { label: 'Отправлено в 1С', type: 'is-success' },
    error: { label: 'Ошибка', type: 'is-danger' },
};

export const actions: ActionTree<ShiftOneCExportState, RootState> = {
    async loadData({ commit, state }, { documentId, silence }: any) {
        if (state.documentId !== documentId) {
            commit('reset', documentId);
        }
        const vm = (this as any)._vm;
        state.loading = !silence;

        try {
            const result = await vm.$db([
                vm.$q(ONEC_PACKAGES)
                    .raw('documents.list')
                    .action({
                        columns: {
                            id: 'ep.id',
                            status: 'ep.status',
                            attempts: 'ep.attempts',
                            external_batch_id: 'ep.external_batch_id',
                            coock_plan_id: 'ep.coock_plan_id',
                            batch_number: 'sacp.number',
                            calculation_version: 'ep.calculation_version',
                            formed_at: 'ep.formed_at',
                            sent_at: 'ep.sent_at',
                            confirmed_at: 'ep.confirmed_at',
                            one_c_doc_number: 'ep.one_c_doc_number',
                            one_c_doc_id: 'ep.one_c_doc_id',
                            one_c_doc_date: 'ep.one_c_doc_date',
                            error_code: 'ep.error_code',
                            error_text: 'ep.error_text',
                        },
                        from: 's:onec_export_packages',
                        from_as: 'ep',
                        joins: [
                            {
                                join: 's:shift_atom_coock_plan',
                                type: 'left',
                                as: 'sacp',
                                on: { $eq: ['sacp.id', 'ep.coock_plan_id'] },
                            },
                        ],
                        where: {
                            $and: [{$eq: ['ep.shift_id', {$: documentId}]}],
                        },
                        orders: ['sacp.number'],
                    }),
            ]);
            const rows = result.getOperation(ONEC_PACKAGES).getContent().documents;
            commit('setPackages', rows);
        } catch (e) {
            // tslint:disable-next-line:no-console
            console.error(e);
            commit('fatalError');
        } finally {
            state.loading = false;
        }
    },

    /**
     * Повторная отправка: сервер пересчитывает ошибочные пакеты смены и
     * возвращает актуальный список; отправку выполнит воркер в ближайшем тике.
     */
    async retry({ state, commit, dispatch }) {
        const vm = (this as any)._vm;
        commit('retrying', true);
        try {
            await vm.$http.post('/api/v1/onec_export/retry', {shift_id: state.documentId});
            await dispatch('loadData', {documentId: state.documentId});
            // воркер отправляет в ближайшем ежеминутном тике — догоняющее
            // обновление статусов (polling на calculated не висит)
            setTimeout(() => {
                if (state.documentId) {
                    dispatch('loadData', {documentId: state.documentId, silence: true});
                }
            }, 70000);
        } catch (e) {
            // tslint:disable-next-line:no-console
            console.error(e);
            const response = e && e.response;
            const message = response && response.data && response.data.error
                ? response.data.error
                : 'При повторной отправке возникли проблемы. Попробуйте позже.';
            throw new Error(message);
        } finally {
            commit('retrying', false);
        }
    },

    /**
     * Пересчёт по требованию (кнопки скачивания XLSX): сервер принудительно
     * пересчитывает пакеты — все партии смены или одну (batchId) — из
     * актуальных данных и возвращает их с payload. Отправки в 1С не
     * происходит; статусы в карточке обновляются по факту пересчёта.
     */
    async recalculate({ state, commit, dispatch }, { batchId }: any = {}) {
        const vm = (this as any)._vm;
        try {
            const response = await vm.$http.post('/api/v1/onec_export/recalculate', {
                shift_id: state.documentId,
                ...(batchId ? {batch_id: batchId} : {}),
            });
            const packages = response.data && response.data.packages;
            if (Array.isArray(packages)) {
                // без payload — чтобы тяжёлый JSON не висел в состоянии карточки
                commit('setPackages', packages.map((p: any) => {
                    const {payload, ...rest} = p;
                    return rest;
                }));
            }
            return packages || [];
        } catch (e) {
            // tslint:disable-next-line:no-console
            console.error(e);
            const response = e && e.response;
            const message = response && response.data && response.data.error
                ? response.data.error
                : 'При пересчёте возникли проблемы. Попробуйте позже.';
            throw new Error(message);
        }
    },
};

export const mutations: MutationTree<ShiftOneCExportState> = {
    reset(state, documentId: any) {
        Object.assign(state, cloneDeep(initState), {documentId});
    },
    setPackages(state, packages: any[]) {
        state.packages = packages;
        state.loaded = true;
    },
    retrying(state, value: boolean) {
        state.retrying = value;
    },
    fatalError(state) {
        state.fatalError = true;
    },
};

export const getters: GetterTree<ShiftOneCExportState, RootState> = {
    /**
     * Общий статус обмена смены и сводка по партиям (раздел 5.1 ТЗ):
     * Не отправлено / В процессе / Успешно / Частично отправлено / Ошибка.
     */
    summary: (state: ShiftOneCExportState) => {
        const packages = state.packages;
        const totals = {
            all: packages.length,
            sent: packages.filter((p: any) => p.status === 'sent').length,
            errors: packages.filter((p: any) => p.status === 'error').length,
        };
        // активная работа (расчёт/отправка) — «В процессе»; рассчитанные и
        // ждущие очереди пакеты при выключенной автоотправке — «Не отправлено»
        const active = packages.filter((p: any) =>
            ['calculating', 'sending'].includes(p.status)).length;

        if (!packages.length) {
            return {key: 'none', label: 'Не отправлено', type: 'is-light', totals};
        }
        if (active > 0) {
            return {key: 'progress', label: 'В процессе', type: 'is-warning', totals};
        }
        if (totals.sent === packages.length) {
            return {key: 'success', label: 'Успешно', type: 'is-success', totals};
        }
        if (totals.sent > 0) {
            return {key: 'partial', label: 'Частично отправлено', type: 'is-warning', totals};
        }
        if (totals.errors > 0) {
            return {key: 'error', label: 'Ошибка', type: 'is-danger', totals};
        }
        return {key: 'none', label: 'Не отправлено', type: 'is-light', totals};
    },

    inProgress: (state: ShiftOneCExportState) => state.packages.some((p: any) =>
        ['calculating', 'sending'].includes(p.status)),
};

export const STORE_KEY = '$_shift_onec_export';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
